import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { ItemAgenda, FiltroAgenda } from './types';
import {
  agruparPorDia,
  agruparPorMes,
  filtrarItens,
  formatarMesAno,
  mesmoMes,
  nomeMes,
} from './utils';
import SeletorMes from './components/SeletorMes';
import BarraFiltros from './components/BarraFiltros';
import BlocoDia from './components/BlocoDia';
import VisaoAno from './components/VisaoAno';
import EstadoVazio from './components/EstadoVazio';

type ModoAgenda = 'mes' | 'ano';

export default function Agenda() {
  const [itens, setItens] = useState<ItemAgenda[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [filtro, setFiltro] = useState<FiltroAgenda>('tudo');
  const [modo, setModo] = useState<ModoAgenda>('mes');
  const [mesAtual, setMesAtual] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [anoAtual, setAnoAtual] = useState(() => new Date().getFullYear());

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        const inicio = new Date(anoAtual, 0, 1);
        const fim = new Date(anoAtual + 1, 0, 1);
        const { data, error } = await supabase
          .from('vw_agenda')
          .select('*')
          .neq('tipo', 'rifa')
          .gte('inicio_em', inicio.toISOString())
          .lt('inicio_em', fim.toISOString())
          .order('inicio_em');
        if (error) throw error;
        if (ativo) setItens((data as ItemAgenda[]) || []);
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar a agenda. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [reload, anoAtual]);

  const filtrados = useMemo(() => filtrarItens(itens, filtro), [itens, filtro]);
  const dias = useMemo(() => agruparPorDia(filtrados), [filtrados]);
  const meses = useMemo(() => agruparPorMes(dias), [dias]);
  const diasDoMes = useMemo(
    () => dias.filter((d) => mesmoMes(d.data, mesAtual)),
    [dias, mesAtual],
  );

  const anterior = () => {
    const novo = new Date(mesAtual.getFullYear(), mesAtual.getMonth() - 1, 1);
    setMesAtual(novo);
    if (novo.getFullYear() !== anoAtual) setAnoAtual(novo.getFullYear());
  };
  const proximo = () => {
    const novo = new Date(mesAtual.getFullYear(), mesAtual.getMonth() + 1, 1);
    setMesAtual(novo);
    if (novo.getFullYear() !== anoAtual) setAnoAtual(novo.getFullYear());
  };
  const anoAnterior = () => setAnoAtual((a) => a - 1);
  const anoProximo = () => setAnoAtual((a) => a + 1);
  const abrirMes = (mes: number) => {
    setMesAtual(new Date(anoAtual, mes, 1));
    setModo('mes');
  };

  return (
    <div className="min-h-screen bg-primary-50">
      <header className="border-b border-secondary-200/60 bg-white">
        <div className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6 md:py-10">
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">
            Agenda Católica de Tupã
          </h1>
          <p className="mt-2 text-sm text-foreground-600 md:text-base">
            Tudo o que está marcado na cidade. Antes de marcar o seu, olhe aqui.
          </p>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-4 py-6 md:px-6">
        {/* Alternador de visão */}
        <div className="flex justify-center">
          <div className="inline-flex items-center rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setModo('mes')}
              className={`cursor-pointer whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                modo === 'mes'
                  ? 'bg-white text-foreground-950'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              Mês
            </button>
            <button
              type="button"
              onClick={() => setModo('ano')}
              className={`cursor-pointer whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                modo === 'ano'
                  ? 'bg-white text-foreground-950'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              Ano
            </button>
          </div>
        </div>

        <div className="mt-4 md:mt-6">
          <BarraFiltros ativo={filtro} onMudar={setFiltro} />
        </div>

        {loading && (
          <div className="mt-8 space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                <div className="h-4 w-48 animate-pulse rounded bg-secondary-100" />
                <div className="mt-3 h-14 animate-pulse rounded-lg bg-secondary-100" />
              </div>
            ))}
          </div>
        )}

        {!loading && erro && (
          <div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-secondary-200 bg-white px-6 py-14 text-center">
            <p className="text-lg font-semibold text-foreground-950">Não foi possível carregar</p>
            <p className="mt-2 text-sm text-foreground-600">{erro}</p>
            <button
              type="button"
              onClick={() => setReload((r) => r + 1)}
              className="mt-6 cursor-pointer whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !erro && modo === 'ano' && (
          <VisaoAno
            ano={anoAtual}
            itens={filtrados}
            onAnterior={anoAnterior}
            onProximo={anoProximo}
            onAbrirMes={abrirMes}
          />
        )}

        {!loading && !erro && modo === 'mes' && (
          <>
            {/* Desktop: mês selecionado */}
            <div className="hidden md:block">
              <SeletorMes data={mesAtual} onAnterior={anterior} onProximo={proximo} />
              {diasDoMes.length === 0 ? (
                <EstadoVazio mes={nomeMes(mesAtual)} />
              ) : (
                <div className="mt-2">
                  {diasDoMes.map((dia) => (
                    <BlocoDia key={dia.chave} dia={dia} />
                  ))}
                </div>
              )}
            </div>

            {/* Mobile: lista contínua com cabeçalhos grudentos */}
            <div className="mt-2 md:hidden">
              {meses.length === 0 ? (
                <EstadoVazio />
              ) : (
                meses.map((mes) => (
                  <div key={mes.chave}>
                    <div className="sticky top-16 z-10 -mx-4 border-b border-secondary-200 bg-white px-4 py-2">
                      <h2 className="text-sm font-bold uppercase tracking-wide text-foreground-900">
                        {formatarMesAno(mes.data)}
                      </h2>
                    </div>
                    <div>
                      {mes.dias.map((dia) => (
                        <BlocoDia key={dia.chave} dia={dia} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}